import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Button, Divider} from '@mui/material';
import Typography from '@mui/material/Typography';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import {generateUUID} from '../../../../utility/uuidUtil';
import PropTypes from 'prop-types';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import {getComponentByType} from './FormBuilderComponents';
import {ComponentCategory, FORM_BUILDER_COMPONENTS, FormBuilderComponentTypes} from './ComponentList';
import ComponentBuilder from './Components/ComponentBuilder';
import {getCalculationSourceOptions} from './Components/Calculation';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import ShowFieldWhen, {matchesRelatesTargetState} from './ShowFieldWhen';
import VisibilityIcon from '@mui/icons-material/Visibility';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ConfirmRemoveDialog from './ConfirmRemoveDialog';
import {toTestId} from '../../../../utility/testIdUtil';
import {useGetRelatedFormQuestionAssignmentQuery} from '../../../../api/services/formAssignmentsService';
import Tooltip from '@mui/material/Tooltip';
import ImageWithConditionalRender from '../../../../shared-components/ImageWithConditionalRender';
import ErrorIcon from '@mui/icons-material/Error';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {COMPONENT_HELP, DEFAULT_QUESTION_BOX_TEXT, QUESTION_BOX_TEXT, SHOW_FIELD_WHEN_HELP} from './componentHelp';
import {useAppHeaderHeight} from '../../../../utility/hooks/useStickyBarOffset';
import {validateAge, validateEmail, validatePhoneNumber, validateUPN} from '../../../../utility/validationUtil';
import moment from 'moment/moment';
import {useGetAdmissionCategoriesQuery} from '../../../../api/services/lookupsService';
import {checkOneAdmittingCategory, isOneAdmittingCategoryQuestion, withEveryCategoryShown} from './admittingCategory';
import {stripHtmlTags} from '../../../../utility/stripHtmlTags';
import {medicineStockErrors} from '../../../../utility/medicines';
import {FORM_ACCESS_LEVELS} from '../../../../constants/formAccessLevels';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined';
import {
    groupIntoRows,
    indexBeforeRow,
    joinRow,
    leaveRow,
    MAX_PER_ROW,
    moveRow,
    placeCopy,
    resizePair,
    tidyRow
} from './formRows';
import FormRow from './FormRow';
import SubmitProblemDialog from './SubmitProblemDialog';

export const availableComponents = [
    FormBuilderComponentTypes.Dropdown,
    FormBuilderComponentTypes.SingleChoice,
    FormBuilderComponentTypes.CheckboxQuestion,
    FormBuilderComponentTypes.MultipleChoice,
    FormBuilderComponentTypes.OfferStatusChoice,
    FormBuilderComponentTypes.BoarderStatusChoice,
    FormBuilderComponentTypes.IntakeChoice,
    FormBuilderComponentTypes.Address,
];

/**
 * The value a Payment field carries once its payment has been confirmed. Written only after the
 * server has verified the payment with Stripe, so it is a record of a completed payment rather than
 * an answer the payer chose.
 */
export const PAYMENT_PAID_VALUE = 'PAID';

const validators = {
    email: validateEmail,
    tel: validatePhoneNumber,
    upn: validateUPN,
    text: (value, isRequired) => {
        const isValid = !isRequired || (value != null && value !== '');

        return {
            isValid,
            message: isValid ? '' : 'This field is required'
        };
    },
};

const validateField = (field, submitData, schema, validateOnSubmit = false) => {
    if (field.relates) {
        const related = schema.find(e => e.id === field.relates.relatesTo);
        const relatedValue = submitData?.[related?.id];
        const conditionMet = matchesRelatesTargetState(
            related,
            relatedValue,
            field.relates.targetState
        );

        if (!conditionMet) {
            return {
                isValid: true,
                error: ''
            };
        }
    }

    if (isOneAdmittingCategoryQuestion(field)) {
        return checkOneAdmittingCategory(submitData?.[field.id]);
    }

    switch (field.type) {
        case FormBuilderComponentTypes.TickBoxGrid:
        case FormBuilderComponentTypes.MultipleChoiceGrid: {
            if (!field.enableValidation) {
                return {
                    isValid: true,
                    error: ''
                };
            }

            const data = submitData?.[field.id];
            if (!data || typeof data !== 'object') {
                return {
                    isValid: false,
                    error: 'Please pick at least one option'
                };
            }

            const schemaRows = field.rows || [];
            const customRows = field.allowCustomRows && Array.isArray(data.__customRows)
                ? data.__customRows
                : [];
            const schemaRowIds = schemaRows.map((r) => r.id);
            const customRowIds = customRows.map((r) => r.id).filter(Boolean);

            const hasEmptyCustomRowTitle = customRows.some((row) => {
                const title = typeof row?.title === 'string' ? row.title : '';
                return title.trim() === '';
            });

            if (hasEmptyCustomRowTitle) {
                return {
                    isValid: false,
                    error: 'Please enter a title for each custom row'
                };
            }

            const rowHasSelection = (rowId) => {
                if (field.type === FormBuilderComponentTypes.TickBoxGrid) {
                    const sel = data[rowId];
                    return Array.isArray(sel) && sel.length > 0;
                }
                const sel = data[rowId];
                return sel != null && sel !== '';
            };

            const allRowIds = [...schemaRowIds, ...customRowIds];
            const gridMissing = allRowIds.some((id) => !rowHasSelection(id));

            return gridMissing
                ? {
                    isValid: false,
                    error: 'Please pick at least one option in each row'
                }
                : {
                    isValid: true,
                    error: ''
                };
        }
        case FormBuilderComponentTypes.CheckboxQuestion:
        case FormBuilderComponentTypes.SingleChoice:
        case FormBuilderComponentTypes.OfferStatusChoice:
        case FormBuilderComponentTypes.IntakeChoice:
        case FormBuilderComponentTypes.LookupDropdown:
        case FormBuilderComponentTypes.EmployeeSelect:
        case FormBuilderComponentTypes.FileUploader: {
            if (!field.enableValidation) {
                return {
                    isValid: true,
                    error: ''
                };
            }
            const hasValue = field.type === FormBuilderComponentTypes.EmployeeSelect
                ? !!submitData?.[field.id]?.id
                : !!submitData?.[field.id];
            return hasValue
                ? {
                    isValid: true,
                    error: ''
                }
                : {
                    isValid: false,
                    error: 'This field is required'
                };
        }
        case FormBuilderComponentTypes.MultiLineInput: {
            const inputs = field.inputs || [];
            const data = submitData?.[field.id];

            const fieldErrors = inputs.reduce((errors, input) => {
                if (!input.enableValidation) return errors;

                const value = data?.[input.id];
                if (!value || !value.trim()) {
                    errors[input.id] = 'This field is required';
                }

                return errors;
            }, {});

            const isValid = Object.keys(fieldErrors).length === 0;

            return {
                isValid,
                error: fieldErrors
            };
        }
        case FormBuilderComponentTypes.SingleText: {
            const data = submitData?.[field.id];
            const mustValidate = field.enableValidation || (data !== '' && data !== undefined);

            if (!mustValidate) {
                return {
                    isValid: true,
                    error: ''
                };
            }

            const validator = validators[field.inputType] ?? ((value) => validators.text(value, field.enableValidation));
            const {
                isValid,
                message
            } = validator(data, field.enableValidation);

            return {
                isValid,
                error: message
            };
        }
        case FormBuilderComponentTypes.UPN: {
            const data = submitData?.[field.id];
            const mustValidate = field.enableValidation || (data !== '' && data !== undefined);

            if (!mustValidate) {
                return {
                    isValid: true,
                    error: ''
                };
            }

            const {
                isValid,
                message
            } = validateUPN(data, field.enableValidation);
            return {
                isValid,
                error: message
            };
        }
        case FormBuilderComponentTypes.PreviousSchoolName: {
            if (!field.enableValidation) {
                return {
                    isValid: true,
                    error: ''
                };
            }
            const data = submitData?.[field.id];
            const hasValue = data != null && data !== '' && data !== undefined;
            return hasValue
                ? {
                    isValid: true,
                    error: ''
                }
                : {
                    isValid: false,
                    error: 'This field is required'
                };
        }
        case FormBuilderComponentTypes.PreviousSchoolEmail: {
            const data = submitData?.[field.id];
            const mustValidate = field.enableValidation || (data !== '' && data !== undefined);

            if (!mustValidate) {
                return {
                    isValid: true,
                    error: ''
                };
            }

            const {
                isValid,
                message
            } = validateEmail(data, field.enableValidation);
            return {
                isValid,
                error: message
            };
        }
        case FormBuilderComponentTypes.Address: {
            if (!validateOnSubmit || !field.enableValidation) {
                return {
                    isValid: true,
                    error: {}
                };
            }

            const data = submitData?.[field.id];
            const fieldErrors = {};

            ['town', 'postcode'].forEach(input => {
                const value = data[input];

                if (!value || value.trim() === '') {
                    fieldErrors[input] = 'This field is required';
                }
            });
            const isValid = Object.keys(fieldErrors).length === 0;

            return {
                isValid,
                error: fieldErrors
            };
        }
        case FormBuilderComponentTypes.NumericInputBox: {
            const raw = submitData?.[field.id];

            const isValidNumber = (s) => /^-?\d+(\.\d+)?$/.test(String(s).trim());

            if (raw === '' || raw === undefined || raw === null) {
                return field.enableValidation
                    ? {
                        isValid: false,
                        error: 'This field is required'
                    }
                    : {
                        isValid: true,
                        error: ''
                    };
            }

            if (!isValidNumber(raw)) {
                return {
                    isValid: false,
                    error: 'Enter a valid number, e.g. -3 or 0.03'
                };
            }

            return {
                isValid: true,
                error: ''
            };
        }
        case FormBuilderComponentTypes.LinearScale: {
            const empty = field.enableValidation && (submitData?.[field.id] === null || submitData?.[field.id] === undefined || submitData[field.id] === '');
            return empty
                ? {
                    isValid: false,
                    error: 'This field is required'
                }
                : {
                    isValid: true,
                    error: ''
                };
        }
        case FormBuilderComponentTypes.QuestionGrid: {
            const data = submitData?.[field.id];
            const fieldErrors = {};
            let hasError = false;

            const isMultiColumn = field.columns && field.columns.length > 0;

            field.inputs?.forEach(input => {
                if (input.required) {
                    const inputValue = data?.[input.id];

                    if (isMultiColumn) {
                        const inputErrors = {};
                        let inputHasError = false;

                        field.columns?.forEach(column => {
                            const columnValue = inputValue?.[column.id];
                            if (!columnValue || (typeof columnValue === 'string' && columnValue.trim() === '')) {
                                inputErrors[column.id] = 'This field is required';
                                inputHasError = true;
                            }
                        });

                        if (inputHasError) {
                            fieldErrors[input.id] = inputErrors;
                            hasError = true;
                        }
                    } else {
                        if (!inputValue || (typeof inputValue === 'string' && inputValue.trim() === '')) {
                            fieldErrors[input.id] = 'This field is required';
                            hasError = true;
                        }
                    }
                }
            });

            return {
                isValid: !hasError,
                error: hasError ? fieldErrors : ''
            };
        }
        case FormBuilderComponentTypes.StripePayment: {
            // A required Stripe payment blocks submission until the money has actually landed. The
            // field is only ever set to PAID by the server confirming the payment, so this cannot be
            // satisfied by anything the payer's browser decides for itself.
            //
            // The older Payment component is not covered by this: it sends the payer out to a link
            // and Admicity never learns whether they paid, so there is nothing to check.
            if (!field.enableValidation) {
                return {
                    isValid: true,
                    error: ''
                };
            }

            const isPaid = submitData?.[field.id] === PAYMENT_PAID_VALUE;

            return isPaid
                ? {
                    isValid: true,
                    error: ''
                }
                : {
                    isValid: false,
                    error: 'This payment must be completed before the form can be submitted'
                };
        }
        case FormBuilderComponentTypes.Separator: {
            return {
                isValid: true,
                error: ''
            };
        }
        // Checked in full on Submit. The question then shows what is missing on each box, and the
        // parent sees each message go as they fix it.
        case FormBuilderComponentTypes.MedicineStock: {
            if (!validateOnSubmit) {
                return {
                    isValid: true,
                    error: ''
                };
            }

            const isValid = medicineStockErrors(submitData?.[field.id]?.length ? submitData[field.id] : [{}])
                .every(messages => messages.length === 0);

            return {
                isValid,
                error: isValid ? '' : 'Please check each medicine box'
            };
        }
        default: {
            const empty = field.enableValidation && (submitData?.[field.id] === undefined || submitData[field.id] === '');
            return empty
                ? {
                    isValid: false,
                    error: 'This field is required'
                }
                : {
                    isValid: true,
                    error: ''
                };
        }
    }
};

const formatAllowedList = (allowed) => {
    const uniq = Array.from(new Set(allowed)).sort((a, b) => a - b);
    if (uniq.length === 1) return `${uniq[0]}`;
    if (uniq.length === 2) return `${uniq[0]} or ${uniq[1]}`;
    return `${uniq.slice(0, -1).join(', ')} or ${uniq[uniq.length - 1]}`;
};

const validateSchemaField = (field) => {
    switch (field.type) {
        case FormBuilderComponentTypes.Date: {
            if (field.minAge == null) {
                return '';
            }

            return validateAge(Number(field.minAge));
        }
        case FormBuilderComponentTypes.DateTime: {
            const minDate = field.minDateTime;
            const maxDate = field.maxDateTime;

            if (minDate && maxDate) {
                return moment(minDate).isAfter(moment(maxDate))
                    ? 'The earliest date and time must be before the latest.'
                    : '';
            }

            return '';
        }
        case FormBuilderComponentTypes.OfferStatusChoice: {
            if (field && (!field.options?.length ||
                !field.options?.every(o => o.value !== '' && o.value != null))) {
                return 'Choose an application status for each option.';
            }

            return '';
        }
        case FormBuilderComponentTypes.LookupDropdown: {
            if (field && (!field.lookupType ||
                field.lookupType === '')) {
                return 'Choose the lookup list for this dropdown.';
            }

            return '';
        }
        case FormBuilderComponentTypes.IntakeChoice: {
            if (field && (!field.options?.length ||
                !field.options?.every(o => o.value !== '' && o.value != null))) {
                return 'Choose an intake for each option.';
            }

            if (field && field.applicationStatusId == null) {
                return 'Choose the application status given to the applicant when the form is submitted.';
            }

            return '';
        }
        case FormBuilderComponentTypes.BoarderStatusChoice: {
            if (field && (!field.options?.length ||
                !field.options?.every(o => o.value !== '' && o.value != null))) {
                return 'Choose a boarder status for each option.';
            }

            return '';
        }
        case FormBuilderComponentTypes.NumericInputBox: {
            const k = field.fieldKey ?? field.key;
            const allowed = field.validation?.allowedValues;

            if (k && k.toLowerCase().includes('priority')) {
                const allowedValues = Array.isArray(allowed) && allowed.length > 0 ? allowed : [1, 2];

                const n = Number(field.priority);
                const valid = Number.isInteger(n) && allowedValues.includes(n);

                if (!valid) {
                    const label = formatAllowedList(allowedValues);

                    if (k === 'parent1Priority') return `Enter ${label} as the Parent 1 priority.`;
                    if (k === 'parent2Priority') return `Enter ${label} as the Parent 2 priority.`;
                    return `Enter ${label} as the priority.`;
                }
            }

            return '';
        }
        case FormBuilderComponentTypes.QuestionGrid: {
            if (field && (!field.inputs?.length || field.inputs.length === 0)) {
                return 'Add at least one question to the grid.';
            }

            if (field && field.inputs?.some(input => !input.title || input.title.trim() === '')) {
                return 'Enter the text of every question in the grid.';
            }

            return '';
        }
        case FormBuilderComponentTypes.StripePayment: {
            // A Stripe Payment question with no amount collects nothing, so it is caught here rather
            // than leaving somebody a button that cannot charge them.
            if (!(Number.isInteger(field.amountPence) && field.amountPence > 0)) {
                return 'Enter the amount to charge.';
            }

            return '';
        }
        case FormBuilderComponentTypes.Separator: {
            return '';
        }
        case FormBuilderComponentTypes.Calculation: {
            const operands = field.operands ?? [];

            if (operands.length === 0) {
                return 'Choose the questions used in the calculation.';
            }

            if (operands.some(operand => !operand.componentId)) {
                return 'Choose a question for every line of the calculation.';
            }

            return '';
        }
        default: {
            return '';
        }
    }
};

const validateCalculationSources = (field, schema) => {
    const available = getCalculationSourceOptions(schema, field.id).map(option => option.id);
    const dangling = (field.operands ?? []).some(operand =>
        operand.componentId && !available.includes(operand.componentId));

    return dangling
        ? 'A question in the calculation is no longer a Numeric Input above it. Choose it again.'
        : '';
};

const validateSchema = (schema) => {
    const errors = {};
    let hasError = false;

    schema.forEach(f => {
        const validationResult = validateSchemaField(f) ||
            (f.type === FormBuilderComponentTypes.Calculation
                ? validateCalculationSources(f, schema)
                : '');

        errors[f.id] = validationResult;

        if (!hasError && validationResult) {
            hasError = true;
        }
    });

    return errors;
};

const MAX_FILE_UPLOADER_COMPONENTS_COUNT = 10;

// Components with no question box of their own (Notes shows its own box).
const NO_QUESTION_BOX_TYPES = new Set(['additionalNotes', 'separator', 'medicineStock', 'schoolLogo']);

const PALETTE_DROPPABLE_PREFIX = 'palette:';
// AD-2691 (Maria, 14 October 2026): her wording.
const PALETTE_HELP = 'Drag and drop the components you need into your form. You can place up to three components on one line.';
const PALETTE_DRAGGABLE_PREFIX = 'palette-item:';

const formComponentsByCategory = FORM_BUILDER_COMPONENTS.reduce((acc, item) => {
    const {category} = item;
    acc[category] = acc[category] || [];
    acc[category].push(item);
    return acc;
}, {});

const toAdmissionCategoryOption = category => ({
    id: String(category.id),
    description: category.description,
    shortDescription: category.shortDescription,
    number: category.number,
    isSubPart: Boolean(category.parentId)
});

const FormBuilder = ({
                         schema,
                         schemaId,
                         onSchemaChange,
                         onSchemaValidationChange,
                         submitMode = false,
                         handleSubmit,
                         handleDownloadAttachment,
                         data,
                         readOnly,
                         isFormArchived,
                         placeholderValues,
                         accessLevel,
                         formId,
                         componentToEditIndex,
                         setComponentToEditIndex,
                         publicForm,
                         isCommunication,
                         audience,
                         parentAudienceType,
                         recipientType,
                         linkedToStudents,
                         linkedToEmployees,
                         triggerSourceAudience,
                         formRequestId,
                         onEmailCheck,
                         blockedForViewer,
                         needsToConfirmSections,
                         profileStudentId,
                         templateMode = false,
                         isWizardMode = false,
                         title,
                         previewMode: controlledPreviewMode,
                         onPreviewModeChange,
                         formTemplateType,
                     }) => {
    if (!schema) return <></>;

    // The form editor shows Preview on its own tab bar and passes the state in; elsewhere the builder keeps it.
    const [internalPreviewMode, setInternalPreviewMode] = useState(false);
    const isPreviewControlled = typeof onPreviewModeChange === 'function';
    const previewMode = isPreviewControlled ? Boolean(controlledPreviewMode) : internalPreviewMode;
    const setPreviewMode = isPreviewControlled ? onPreviewModeChange : setInternalPreviewMode;

    // The component sidebar stays in view below the bars at the top of the window while the page
    // scrolls, with its own scrollbar when the list is longer than the window (Maria, 14 October 2026).
    // AD-2703 and AD-2705: it is held there by CSS position sticky, which the browser draws with the page, so it
    // does not flicker, and the page ends at the end of the form. Its top is the height of the logo bar and the
    // editor's tab bar (useStickyBarOffset); inside the Create Form box it is the top of the box.
    const paletteRowRef = useRef(null);
    const paletteRef = useRef(null);
    const isPaletteShown = !submitMode && !previewMode && !isCommunication;
    useAppHeaderHeight(isPaletteShown && !isWizardMode);
    const paletteTop = isWizardMode
        ? '16px'
        : 'calc(var(--app-header-height, 0px) + var(--sticky-bar-height, 0px) + 16px)';

    const [submitData, setSubmitData] = useState({});
    const [currentComponent, setCurrentComponent] = useState(undefined);
    const [relatedComponents, setRelatedComponents] = useState([]);
    const [componentToConfirmRemove, setComponentToConfirmRemove] = useState(undefined);
    const [formPlaceholders, setFormPlaceholders] = useState(undefined);
    const [errors, setErrors] = useState({});
    const [schemaErrors, setSchemaErrors] = useState({});
    const {data: relatedFormQuestionAssignData} = useGetRelatedFormQuestionAssignmentQuery(formId, {skip: submitMode || !formId});
    const {data: admissionCategories = []} = useGetAdmissionCategoriesQuery(undefined,
        {skip: submitMode || templateMode});
    const schoolLogoComponent = schema?.find(x => x.type === 'schoolLogo');
    const [warnings, setWarnings] = useState({});
    const [drafts, setDrafts] = useState({});
    const isParentSubmissionBlocked = submitMode && !!blockedForViewer;
    const setWarningFor = (componentId, message) => {
        if (!componentId) return;

        setWarnings(prev => {
            const next = message ?? '';
            if ((prev[componentId] ?? '') === next) return prev;
            return {
                ...prev,
                [componentId]: next
            };
        });
    };

    useEffect(() => {
        if (isFormArchived) {
            setPreviewMode(true);

            if (setComponentToEditIndex) {
                setComponentToEditIndex(undefined);
            }
        }
    }, [isFormArchived, setComponentToEditIndex]);

    useEffect(() => {
        setRelatedComponents(schema.filter(e => availableComponents.includes(e.type) && e.id !== currentComponent?.id));
    }, [schema]);

    // A form made from a template carries an Admissions Categories question with no categories of its
    // own. It is given the school's own list, so every category on it is one the school added.
    useEffect(() => {
        if (submitMode || templateMode || !onSchemaChange || admissionCategories.length === 0) {
            return;
        }

        const isEmptyCategoriesQuestion = component =>
            component.key === 'admissionCategories' && !(component.options?.length);

        if (!schema.some(isEmptyCategoriesQuestion)) {
            return;
        }

        onSchemaChange(schema.map(component => isEmptyCategoriesQuestion(component)
            ? {...component, options: admissionCategories.map(toAdmissionCategoryOption)}
            : component));
    }, [admissionCategories, schema]);

    useEffect(() => {
        if (submitMode) {
            setFormPlaceholders(placeholderValues);
        }
    }, [placeholderValues]);

    const editorPlaceholderValues = useMemo(() => {
        const basePlaceholderValues = formPlaceholders ?? placeholderValues ?? {};
        const hasBasePlaceholderValues = Object.keys(basePlaceholderValues).length > 0;

        if (!hasBasePlaceholderValues && triggerSourceAudience == null) {
            return undefined;
        }

        return {
            ...basePlaceholderValues,
            placeholderMetadata: {
                ...(basePlaceholderValues.placeholderMetadata ?? {}),
                triggerSourceAudience: triggerSourceAudience ?? null,
            },
        };
    }, [formPlaceholders, placeholderValues, triggerSourceAudience]);

    useEffect(() => {
        if (submitMode && data && Object.keys(data).length > 0) {
            setSubmitData({ ...data });
        }
    }, [submitMode, data]);

    const answersKey = formRequestId ? `admicity:answers:${formRequestId}` : null;

    useEffect(() => {
        if (!submitMode || !answersKey) return;

        const params = new URLSearchParams(window.location.search);

        if (params.get('paid') !== '1') return;

        try {
            const saved = window.sessionStorage.getItem(answersKey);

            if (saved) setSubmitData(prev => ({...JSON.parse(saved), ...prev}));
        } catch {
            // A browser that refuses session storage simply loses the answers, as before.
        }
    }, [submitMode, answersKey]);

    useEffect(() => {
        if (!submitMode || !answersKey || Object.keys(submitData).length === 0) return;

        try {
            const keep = Object.fromEntries(
                Object.entries(submitData).filter(([, v]) => typeof v !== 'object' || v === null));

            window.sessionStorage.setItem(answersKey, JSON.stringify(keep));
        } catch {
            // Not being able to remember the answers is not a reason to stop the form working.
        }
    }, [submitMode, answersKey, submitData]);

    useEffect(() => {
        const validation = !readOnly
            ? validateSchema(schema)
            : {};

        if (onSchemaValidationChange) {
            onSchemaValidationChange(validation);
        }

        setSchemaErrors(validation);
    }, [schemaId, schema]);

    const handleSetSchema = (newSchema) => {
        const validation = !readOnly
            ? validateSchema(newSchema)
            : {};

        onSchemaChange(newSchema);

        if (onSchemaValidationChange) {
            onSchemaValidationChange(validation);
        }

        setSchemaErrors(validation);
    };

    const handleChangeEdit = ({
                                  index,
                                  description,
                                  bookingLink,
                                  paymentLink,
                                  amountPence,
                                  priority,
                                  validation,
                                  enableValidation,
                                  option,
                                  optionId,
                                  isAdd,
                                  isRemove,
                                  files,
                                  listKey,
                                  startFrom,
                                  endAt,
                                  startLabel,
                                  endLabel,
                                  formattedDescription,
                                  lookupType,
                                  minAge,
                                  minDateTime,
                                  maxDateTime,
                                  inputType,
                                  applicationStatusId,
                                  alignment,
                                  allowCustomRows,
                                  prefillFrom,
                                  label,
                                  medicineLookupId,
                                  imageWidthPercent,
                                  onlyOneCategory
                              }) => {
        const newForm = [...schema];
        const updatedItem = structuredClone(newForm[index]);

        if (onlyOneCategory !== undefined) {
            updatedItem.onlyOneCategory = onlyOneCategory;
            if (onlyOneCategory) {
                updatedItem.options = withEveryCategoryShown(updatedItem.options);
            }
        }

        if (description !== undefined) {
            updatedItem.description = description;
        }
        if (label !== undefined) {
            updatedItem.label = label;
        }
        if (medicineLookupId !== undefined) {
            updatedItem.medicineLookupId = medicineLookupId;
        }
        if (formattedDescription !== undefined) {
            updatedItem.formattedDescription = formattedDescription;
        }
        if (bookingLink !== undefined) {
            updatedItem.bookingLink = bookingLink;
        }
        if (paymentLink !== undefined) {
            updatedItem.paymentLink = paymentLink;
        }
        if (amountPence !== undefined) {
            // Cleared rather than stored as null, so a Payment question with no amount reads the same
            // in the schema as one that never had one.
            if (amountPence === null) {
                delete updatedItem.amountPence;
            } else {
                updatedItem.amountPence = amountPence;
            }
        }
        if (priority === undefined || priority === '') {
            delete updatedItem.priority
        } else {
            updatedItem.priority = priority;
        }
        if (validation !== undefined) {
            updatedItem.validation = validation;
        }
        if (enableValidation !== undefined) {
            updatedItem.enableValidation = enableValidation;
        }
        if (option !== undefined && !listKey) {
            const optIndex = updatedItem.options.findIndex(e => e.id === optionId);
            if (optIndex !== -1) {
                const opt = {...updatedItem.options[optIndex]};

                if (Object.prototype.hasOwnProperty.call(option, 'description')) {
                    opt.description = option.description;
                }

                // A school hides a choice on this form. The choice keeps its id, so a tick still
                // lands where it did.
                if (Object.prototype.hasOwnProperty.call(option, 'hidden')) {
                    opt.hidden = option.hidden;
                }

                if (Object.prototype.hasOwnProperty.call(option, 'value')) {
                    opt.value = option.value;
                }
                const options = [...updatedItem.options];
                options[optIndex] = opt;
                updatedItem.options = options;
            }
        }
        if (isAdd && !listKey) {
            updatedItem.options = [
                ...updatedItem.options,
                {id: generateUUID()}
            ];
        }
        if (isRemove && !listKey) {
            const relatedComponent = schema.find(e => e.relates && e.relates.targetState === optionId);
            if (relatedComponent) {
                setComponentToConfirmRemove({
                    component: relatedComponent,
                    handleRemove: () => removeOptionModal(updatedItem.id, optionId)
                });
            } else {
                updatedItem.options = updatedItem.options.filter(e => e.id !== optionId);
            }
        }
        if (listKey) {
            if (!updatedItem[listKey]) {
                updatedItem[listKey] = [];
            }

            if (isRemove) {
                updatedItem[listKey] = updatedItem[listKey].filter(
                    (item, idx) => idx !== optionId && item.id !== optionId
                );
            } else if (isAdd) {
                const newOption =
                    typeof option === 'object' && option !== null
                        ? option
                        : {
                            title: '',
                            id: generateUUID()
                        };

                updatedItem[listKey] = [...updatedItem[listKey], newOption];
            } else {
                const itemIndex =
                    typeof optionId === 'number'
                        ? optionId
                        : updatedItem[listKey].findIndex((i) => i.id === optionId);

                if (itemIndex !== -1) {
                    const current = updatedItem[listKey][itemIndex];
                    updatedItem[listKey][itemIndex] =
                        typeof option === 'string'
                            ? {
                                ...current,
                                title: option
                            }
                            : {...current, ...option};
                }
            }
        }
        if (files !== undefined) {
            updatedItem.attachments = files;
        }
        if (startFrom !== undefined) {
            updatedItem.startFrom = startFrom;
        }
        if (endAt !== undefined) {
            updatedItem.endAt = endAt;
        }
        if (startLabel !== undefined) {
            updatedItem.startLabel = startLabel;
        }
        if (endLabel !== undefined) {
            updatedItem.endLabel = endLabel;
        }
        if (lookupType !== undefined) {
            updatedItem.lookupType = lookupType;
        }
        if (minAge !== undefined) {
            updatedItem.minAge = minAge;
        }
        if (minDateTime !== undefined) {
            updatedItem.minDateTime = minDateTime;
        }
        if (maxDateTime !== undefined) {
            updatedItem.maxDateTime = maxDateTime;
        }
        if (imageWidthPercent !== undefined) {
            updatedItem.imageWidthPercent = imageWidthPercent;
        }

        if (inputType !== undefined) {
            updatedItem.inputType = inputType;
        }
        if (applicationStatusId !== undefined) {
            updatedItem.applicationStatusId = applicationStatusId;
        }
        if (alignment !== undefined) {
            updatedItem.alignment = alignment;
        }
        if (allowCustomRows !== undefined) {
            updatedItem.allowCustomRows = allowCustomRows;
        }
        if (prefillFrom !== undefined) {
            if (prefillFrom && prefillFrom.formId && prefillFrom.componentId) {
                updatedItem.prefillFrom = {
                    formId: prefillFrom.formId,
                    componentId: prefillFrom.componentId
                };
            } else if (prefillFrom && prefillFrom.formId) {
                updatedItem.prefillFrom = {
                    formId: prefillFrom.formId,
                    componentId: prefillFrom.componentId ?? null
                };
            } else {
                delete updatedItem.prefillFrom;
            }
        }

        newForm[index] = updatedItem;
        handleSetSchema(newForm);
    };

    const getDraftDescriptionHtml = useCallback((componentId, fallbackHtml) => {
        return Object.prototype.hasOwnProperty.call(drafts, componentId)
            ? drafts[componentId]
            : fallbackHtml;
    }, [drafts]);

    const setDraftDescriptionHtml = useCallback((componentId, html) => {
        setDrafts(prev => (prev[componentId] === html
            ? prev
            : {
                ...prev,
                [componentId]: html
            }));
    }, []);

    const commitDraftDescriptionToSchema = useCallback((componentId) => {
        const html = drafts[componentId];
        if (html === undefined) return;

        const index = schema.findIndex(x => x.id === componentId);
        if (index === -1) return;

        handleChangeEdit({
            index,
            description: stripHtmlTags(html),
            formattedDescription: html,
        });

        setDrafts(prev => {
            if (!Object.prototype.hasOwnProperty.call(prev, componentId)) return prev;
            const next = {...prev};
            delete next[componentId];
            return next;
        });
    }, [drafts, schema, handleChangeEdit]);

    const handleShowFieldWhen = (id, relatesTo, targetState) => {
        handleDialogClose();
        const newForm = JSON.parse(JSON.stringify(schema));
        let updatedComponent = newForm.find(e => e.id === id);

        if (relatesTo && targetState !== undefined) {
            updatedComponent.relates = {
                relatesTo,
                targetState
            };
        } else {
            delete updatedComponent.relates;
        }

        handleSetSchema(newForm);
    };

    const handleChangeSubmit = ({
                                    id,
                                    value,
                                    optionIndex,
                                    optionValue,
                                    radioValue
                                }) => {
        if (readOnly) return;

        setSubmitData(prev => {
            let newSubmitData = {...prev};

            if (value !== undefined) {
                newSubmitData[id] = value;
            } else if (optionIndex !== undefined && optionValue !== undefined) {
                if (!newSubmitData[id]) {
                    newSubmitData[id] = [];
                }

                newSubmitData[id] = optionValue ? [...newSubmitData[id], optionIndex] : newSubmitData[id].filter(o => o !== optionIndex);
                if (newSubmitData[id].length === 0) {
                    newSubmitData[id] = undefined;
                }
            } else if (radioValue !== undefined) {
                if (radioValue === null) {
                    delete newSubmitData[id]
                } else {
                    newSubmitData[id] = radioValue;
                }
            }

            schema?.forEach(field => {
                if (!checkIfVisible(field.relates, newSubmitData)) {
                    delete newSubmitData[field.id];
                }
            });

            const field = schema.find(field => field.id === id);

            if (field) {
                setErrors(prev => ({
                    ...prev,
                    [field.id]: validateField(field, newSubmitData, schema)
                }));
            }

            return newSubmitData;
        });
    };
    const applyDraftsToSchema = useCallback((baseSchema) => {
        if (!drafts || Object.keys(drafts).length === 0) return baseSchema;

        let changed = false;

        const next = baseSchema.map(item => {
            if (!Object.prototype.hasOwnProperty.call(drafts, item.id)) return item;

            const html = drafts[item.id] ?? '';
            const nextItem = {
                ...item,
                description: stripHtmlTags(html),
                formattedDescription: html
            };

            changed = true;
            return nextItem;
        });

        return changed ? next : baseSchema;
    }, [drafts]);

    const removeComponent = (componentId) => {
        const componentToRemove = schema.find(e => e.id === componentId);
        if (!componentToRemove) return;

        const related = schema.filter(e => e.relates?.relatesTo === componentId);

        if (related.length > 0) {
            setComponentToConfirmRemove({
                components: related,
                handleRemove: () => removeComponentModal(componentId)
            });
        } else {
            setComponentToConfirmRemove({
                components: [],
                handleRemove: () => removeComponentModal(componentId)
            });
        }
    };

    // The component being edited stays open when the order of the form changes.
    const keepEditing = (before, after) => {
        if (componentToEditIndex == null || componentToEditIndex < 0) return;
        const editingId = before[componentToEditIndex]?.id;
        const at = after.findIndex(component => component.id === editingId);
        setComponentToEditIndex(at === -1 ? undefined : at);
    };

    // AD-2691: moving a row, putting components side by side and changing their widths.
    const changeLayout = (change) => {
        const hasDrafts = Object.keys(drafts).length > 0;
        const base = hasDrafts ? applyDraftsToSchema(schema) : schema;
        const next = change(base);
        if (next === base) return;

        if (hasDrafts) setDrafts({});
        handleSetSchema(next);
        keepEditing(base, next);
    };

    const builderRows = useMemo(() => groupIntoRows(schema ?? []), [schema]);

    const onDragEnd = (result) => {
        const fromPalette = result.source.droppableId.startsWith(PALETTE_DROPPABLE_PREFIX);

        // Dropped on another component: the two go side by side on one row.
        if (result.combine) {
            const targetRow = builderRows.find(row => row.key === result.combine.draggableId);
            if (!targetRow) return;
            const targetId = targetRow.items[0].field.id;

            if (fromPalette) {
                if (targetRow.items.length < MAX_PER_ROW) {
                    addComponent(result.draggableId.slice(PALETTE_DRAGGABLE_PREFIX.length), undefined, targetId);
                }
                return;
            }

            const sourceRow = builderRows.find(row => row.key === result.draggableId);
            if (!sourceRow || sourceRow.items.length + targetRow.items.length > MAX_PER_ROW) return;
            changeLayout(base => sourceRow.items.reduce((next, {field}) => joinRow(next, field.id, targetId), base));
            return;
        }

        if (!result.destination) return;

        if (fromPalette) {
            if (result.destination.droppableId === 'droppable') {
                addComponent(
                    result.draggableId.slice(PALETTE_DRAGGABLE_PREFIX.length),
                    indexBeforeRow(schema, result.destination.index)
                );
            }
            return;
        }

        if (result.source.index === result.destination.index) return;

        changeLayout(base => moveRow(base, result.source.index, result.destination.index));
    };

    // The widths shown while the border between two components is being dragged.
    const [resizing, setResizing] = useState(null);

    const startResize = (event, leftField, rightField) => {
        event.preventDefault();
        event.stopPropagation();
        const rowWidth = event.currentTarget.parentElement?.getBoundingClientRect().width || 1;
        const startX = event.clientX;
        const startWidth = leftField.layout.width;
        let latest = startWidth;

        const onMove = (moveEvent) => {
            latest = startWidth + ((moveEvent.clientX - startX) / rowWidth) * 100;
            setResizing(resizePair(schema, leftField.id, rightField.id, latest));
        };
        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            setResizing(null);
            changeLayout(base => resizePair(base, leftField.id, rightField.id, latest));
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const widthOf = (field) => {
        const shown = resizing?.find(component => component.id === field.id) ?? field;
        return shown.layout?.width ?? 100;
    };

    const removeComponentModal = (id) => {
        const hasDrafts = Object.keys(drafts).length > 0;
        const base = hasDrafts ? applyDraftsToSchema(schema) : schema;

        if (hasDrafts) setDrafts({});

        const newForm = JSON.parse(JSON.stringify(base));

        newForm.forEach(e => {
            if (e.relates && e.relates.relatesTo === id) {
                delete e.relates;
            }
        });

        const removed = newForm.find(e => e.id === id);
        handleSetSchema(tidyRow(newForm.filter((e) => e.id !== id), removed?.layout?.rowId));
        setComponentToConfirmRemove(undefined);
    };

    const removeOptionModal = (componentId, optionId) => {
        const hasDrafts = Object.keys(drafts).length > 0;
        const base = hasDrafts ? applyDraftsToSchema(schema) : schema;
        if (hasDrafts) setDrafts({});

        const newForm = JSON.parse(JSON.stringify(base));

        newForm.forEach(e => {
            if (e.relates && e.relates.targetState === optionId) delete e.relates;
        });

        const componentIndex = newForm.findIndex(e => e.id === componentId);
        if (componentIndex !== -1) {
            newForm[componentIndex].options =
                (newForm[componentIndex].options || []).filter(o => o.id !== optionId);
        }

        handleSetSchema(newForm);
        setComponentToConfirmRemove(undefined);
    };

    // atIndex is where a component dragged from the sidebar was dropped; a click adds it at the end.
    // besideId is the component a sidebar component was dropped on, to go next to it on the same row.
    const addComponent = (type, atIndex, besideId) => {
        const newComponent = {
            ...getComponentByType(type),
            enableValidation: type === 'signature',
            id: generateUUID()
        };

        // The options are the school's own admissions categories, taken from Settings at the moment
        // the question is added. The option id is the category id, so a tick lands on the right
        // category with nothing to look up at submission.
        if (type === FormBuilderComponentTypes.AdmissionCategories) {
            newComponent.options = admissionCategories.map(toAdmissionCategoryOption);

        }

        const filteredSchema = schema.filter(component => component.type !== 'schoolLogo');
        const position = atIndex === undefined
            ? filteredSchema.length
            : Math.max(0, Math.min(atIndex, filteredSchema.length));

        let next = [
            ...filteredSchema.slice(0, position),
            newComponent,
            ...filteredSchema.slice(position),
            ...schema.filter(component => component.type === 'schoolLogo')
        ];
        if (besideId) next = joinRow(next, newComponent.id, besideId);

        keepEditing(schema, next);
        handleSetSchema(next);
    };

    const regenerateIdsDeep = (value) => {
        if (Array.isArray(value)) {
            return value.map(regenerateIdsDeep);
        }

        if (value && typeof value === 'object') {
            return Object.entries(value).reduce((acc, [key, nestedValue]) => {
                if (key === 'id') {
                    acc[key] = generateUUID();
                } else {
                    acc[key] = regenerateIdsDeep(nestedValue);
                }

                return acc;
            }, {});
        }

        return value;
    };

    const cloneComponent = (componentId) => {
        const hasDrafts = Object.keys(drafts).length > 0;
        const base = hasDrafts ? applyDraftsToSchema(schema) : schema;
        if (hasDrafts) setDrafts({});

        const sourceIndex = base.findIndex(component => component.id === componentId);
        if (sourceIndex === -1) return;

        const clonedComponent = regenerateIdsDeep(structuredClone(base[sourceIndex]));
        // AD-2691: the copy goes on a row of its own, after the row of the component copied.
        const nextSchema = placeCopy(base, componentId, clonedComponent);
        handleSetSchema(nextSchema);
        keepEditing(base, nextSchema);
    };

    const hasComponentKeyAttributeValue = (component) => {
        const keyAttribute = component?.fieldKey ?? component?.key;
        return keyAttribute != null && String(keyAttribute).trim() !== '';
    };

    const handleDialogClose = () => {
        setCurrentComponent(undefined);
    };

    const buildEditRows = (f, index) => {
        const relatedComponent = relatedFormQuestionAssignData?.find(e => e.relatedComponentId === f.id);
        const rowIndex = builderRows.findIndex(row => row.items.some(item => item.field.id === f.id));
        return (
            // AD-2689 (Maria): the icons take no more height than the name, so the question box sits right under it.
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" sx={{'& .MuiIconButton-root': {p: 0.25}}}>
                {/* Medicine at School keeps its question in its label, so its heading is the component's name. */}
                <Typography>{index + 1}. {f.type === FormBuilderComponentTypes.MedicineStock ? 'Medicine at School' : f.label}</Typography>
                <Box display="flex" alignItems="center">
                    {/* AD-2689 (Maria): a clear way to finish editing, in the same row as the icons. */}
                    {!isFormArchived && componentToEditIndex === index && (
                        <Button
                            variant="contained"
                            size="small"
                            data-testid={toTestId('builder-finish', index + 1)}
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                                event.stopPropagation();
                                setComponentToEditIndex(undefined);
                            }}
                            sx={{mr: 1, boxShadow: 'none', whiteSpace: 'nowrap'}}
                        >
                            Finish Editing
                        </Button>
                    )}
                    {!isFormArchived && (
                        <Tooltip title="Edit component">
                        <IconButton
                            data-testid={toTestId('builder-edit', index + 1)}
                            onClick={(event) => {
                                event.stopPropagation();
                                setComponentToEditIndex(componentToEditIndex === index ? undefined : index);
                            }}
                            onDoubleClick={(event) => event.stopPropagation()}
                            onMouseDown={(event) => event.stopPropagation()}
                        >
                            <EditIcon/>
                        </IconButton>
                        </Tooltip>
                    )}
                    {!isFormArchived && !isCommunication && !hasComponentKeyAttributeValue(f) && (
                        <Tooltip title="Clone component">
                            <IconButton
                                data-testid={toTestId('builder-clone', index + 1)}
                                onClick={() => cloneComponent(f.id)}
                            >
                                <ContentCopyOutlinedIcon/>
                            </IconButton>
                        </Tooltip>
                    )}
                    {!isFormArchived && !isCommunication && !f.disableDelete && (
                        <Tooltip
                            title={relatedComponent
                                ? `This question cannot be deleted as it is currently used in the ${relatedComponent?.relatedFormName} form.`
                                : 'Delete component'}
                        >
                            <IconButton data-testid={toTestId('builder-delete', index + 1)}>
                                <DeleteIcon
                                    onClick={() => relatedComponent ? {} : removeComponent(f.id)}
                                />
                            </IconButton>
                        </Tooltip>
                    )}
                    {!isFormArchived &&
                        relatedComponents.filter(c => c.id !== f.id).length > 0 &&
                        !f.disableHide && (
                            <Tooltip title={SHOW_FIELD_WHEN_HELP} arrow>
                                <IconButton data-testid={toTestId('builder-show-when', index + 1)}>
                                    <VisibilityIcon
                                        color={f.relates ? 'primary' : ''}
                                        onClick={() => setCurrentComponent(f)}
                                    />
                                </IconButton>
                            </Tooltip>
                        )}
                    {/* AD-2691: a component next to another can go back onto a row of its own. */}
                    {!isFormArchived && !isCommunication && f.layout?.rowId && (
                        <Tooltip title="Move to its own row">
                            <IconButton
                                data-testid={toTestId('builder-own-row', index + 1)}
                                onClick={() => changeLayout(base => leaveRow(base, f.id))}
                            >
                                <CallSplitOutlinedIcon/>
                            </IconButton>
                        </Tooltip>
                    )}
                    {/* The arrows move the whole row. */}
                    {rowIndex > 0 && !isFormArchived && (
                        <IconButton>
                            <ArrowUpwardOutlinedIcon onClick={() => changeLayout(base => moveRow(base, rowIndex, rowIndex - 1))}/>
                        </IconButton>
                    )}
                    {rowIndex !== -1 && rowIndex < builderRows.length - 1 && !isFormArchived && (
                        <IconButton>
                            <ArrowDownwardOutlinedIcon onClick={() => changeLayout(base => moveRow(base, rowIndex, rowIndex + 1))}/>
                        </IconButton>
                    )}
                </Box>
            </Box>
        );
    };

    const checkIfVisible = (relates, submitData) => {
        if (relates) {
            const relatedField = schema.find(e => e.id === relates.relatesTo);
            const relatedComponent = readOnly ? data[relates.relatesTo] : submitData[relates.relatesTo];

            return matchesRelatesTargetState(relatedField, relatedComponent, relates.targetState);
        }
        return true;
    };

    // AD-2692: why the form was not submitted, shown in a pop-up until it is closed.
    const [submitProblems, setSubmitProblems] = useState(null);
    const questionText = (field) => {
        const text = (stripHtmlTags(field.description || '') || field.label || '').trim();
        return text.length > 120 ? `${text.slice(0, 117)}...` : text;
    };
    const isEmptyAnswer = (value) => value === undefined || value === null || value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);
    const goToQuestion = (fieldId) => {
        setSubmitProblems(null);
        setTimeout(() => document.getElementById(`form-field-${fieldId}`)?.scrollIntoView({behavior: 'smooth', block: 'center'}), 50);
    };
    const confirmSectionsList = String(needsToConfirmSections || '')
        .split(',')
        .map(section => section.trim())
        .filter(Boolean);

    const onSubmit = () => {
        if (isParentSubmissionBlocked) {
            setSubmitProblems({sections: confirmSectionsList});
            return;
        }

        const validation = {};
        let isError = false;

        schema.forEach(f => {
            const validationResult = validateField(f, submitData, schema, true);

            if (!isError && !validationResult.isValid) {
                isError = true;
            }

            validation[f.id] = validationResult;
        });

        if (isError) {
            setErrors(validation);
            const problems = {missing: [], format: [], payment: false, medicine: []};
            schema.forEach(field => {
                if (validation[field.id]?.isValid !== false) return;
                if ([FormBuilderComponentTypes.Payment, FormBuilderComponentTypes.StripePayment].includes(field.type)) {
                    problems.payment = true;
                } else if (field.type === FormBuilderComponentTypes.MedicineStock) {
                    // Walkthrough finding 42: the pop-up links to the medicine question, like the others.
                    problems.medicine.push({id: field.id, text: questionText(field), error: validation[field.id]?.error});
                } else if (isEmptyAnswer(submitData?.[field.id])) {
                    problems.missing.push({id: field.id, text: questionText(field)});
                } else {
                    problems.format.push({id: field.id, text: questionText(field), error: validation[field.id]?.error});
                }
            });
            setSubmitProblems(problems);

            return;
        }

        const hiddenFieldIds = schema
            ?.filter(field => !checkIfVisible(field.relates, submitData))
            .map(field => field.id);
        const filteredData = Object.keys(submitData)
            .filter(key => !hiddenFieldIds.includes(key))
            .reduce((acc, key) => {
                acc[key] = submitData[key];
                return acc;
            }, {});

        handleSubmit(filteredData, JSON.stringify(formPlaceholders));
    };

    // AD-2689 (Maria, 14 October 2026): a component that cannot work on this form's type and audience is not
    // offered. What each one needs, from the code that acts on the answers when a form is submitted:
    // - Intake Choice and Boarder Status Choice create the applicant's intake group and boarder status, which
    //   only an application or acceptance form does.
    // - Medicine Dispense lists a child's medicines for a member of staff, so it needs a staff form linked to
    //   a student.
    // - Application Status Choice, Admissions Categories, Medicine at School and the Student Profile Attributes
    //   update a child's profile, so the form must be about a child.
    // - Employee Select and Student Photo are for private forms only.
    // The super admin's template screen offers every component.
    const isPublicForm = accessLevel === FORM_ACCESS_LEVELS.public;
    const isApplicationForm = isPublicForm && formTemplateType === 'application';
    const isAboutAChild = isApplicationForm || (
        !isPublicForm &&
        !(Number(audience) === FORM_AUDIENCE_TYPE.employees && !linkedToStudents) &&
        !(Number(audience) === FORM_AUDIENCE_TYPE.externalUsers && recipientType !== 'student')
    );
    const isComponentOffered = ({type}) => {
        if (templateMode) return true;

        switch (type) {
            // The Lookup Dropdown only updates the profile on the Admicity application, acceptance and add-contact
            // templates, where each one is tied to a field (AD-1433). No ticket asks for it elsewhere, so it is not
            // offered in the sidebar; the templates keep theirs (Maria, 15 October 2026).
            case FormBuilderComponentTypes.LookupDropdown:
                return false;
            case FormBuilderComponentTypes.EmployeeSelect:
            case FormBuilderComponentTypes.StudentPhoto:
                return accessLevel === FORM_ACCESS_LEVELS.private;
            case FormBuilderComponentTypes.IntakeChoice:
            case FormBuilderComponentTypes.BoarderStatusChoice:
                return isApplicationForm;
            case FormBuilderComponentTypes.MedicineDispense:
                return Number(audience) === FORM_AUDIENCE_TYPE.employees && !!linkedToStudents;
            case FormBuilderComponentTypes.OfferStatusChoice:
                return isAboutAChild && !isPublicForm;
            // Medicine at School and UPN are saved to a child already in the school's records, never from an
            // application form.
            case FormBuilderComponentTypes.MedicineStock:
            case FormBuilderComponentTypes.UPN:
                return isAboutAChild && !isPublicForm;
            case FormBuilderComponentTypes.AdmissionCategories:
            case FormBuilderComponentTypes.PreviousSchoolName:
            case FormBuilderComponentTypes.PreviousSchoolEmail:
                return isAboutAChild;
            default:
                return true;
        }
    };

    // One component in the sidebar: drag it into the form, or click on it to add it at the end.
    const renderPaletteItem = ({type, description}, index) => {
        const isUploader = type === FormBuilderComponentTypes.FileUploader;
        const limitExceeded = isUploader && schema.filter(
            (x) => x.type === FormBuilderComponentTypes.FileUploader,
        ).length >= MAX_FILE_UPLOADER_COMPONENTS_COUNT;
        const disabled = isFormArchived || limitExceeded || isCommunication;

        const row = (props = {}) => (
            <Box
                {...props}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 0.5,
                    py: 0.25,
                    mb: 0.5,
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: '#ffffff',
                    color: disabled ? 'text.disabled' : '#186BBE',
                    cursor: disabled ? 'default' : 'grab',
                    '&:hover': disabled ? {} : {borderColor: '#186BBE', backgroundColor: '#f5f9fd'},
                }}
            >
                <DragIndicatorIcon fontSize="small" sx={{color: disabled ? 'text.disabled' : '#9ca3af'}}/>
                {/* AD-2689 (Maria, 14 October 2026): how to use the sidebar is a pop-up over each component. */}
                <Tooltip title={disabled ? '' : PALETTE_HELP} placement="right" enterDelay={400}>
                    <Typography variant="body2" sx={{flex: 1, fontWeight: 500}}>{description}</Typography>
                </Tooltip>
                {limitExceeded && (
                    <Tooltip title="Maximum of 10 file upload fields allowed on the form">
                        <ErrorIcon color="warning" fontSize="small"/>
                    </Tooltip>
                )}
                {COMPONENT_HELP[type] && (
                    <Tooltip title={COMPONENT_HELP[type]} placement="right" arrow>
                        <InfoOutlinedIcon
                            fontSize="small"
                            data-testid={toTestId('builder-help', type)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{color: 'text.secondary', cursor: 'help'}}
                        />
                    </Tooltip>
                )}
            </Box>
        );

        return (
            <Draggable
                key={type}
                draggableId={`${PALETTE_DRAGGABLE_PREFIX}${type}`}
                index={index}
                isDragDisabled={disabled}
            >
                {(provided, snapshot) => (
                    <>
                        <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                        >
                            {row({
                                'data-testid': toTestId('builder-add', type),
                                role: 'button',
                                'aria-disabled': disabled,
                                onClick: () => {
                                    if (!disabled) addComponent(type);
                                },
                            })}
                        </Box>
                        {snapshot.isDragging && row()}
                    </>
                )}
            </Draggable>
        );
    };

    const showPalette = isPaletteShown;

    const submitFields = (schema ?? [])
        .filter(e => checkIfVisible(e.relates, submitData) && e.showEditModeOnly !== true);
    const previewFields = (schema ?? [])
        .filter(e => checkIfVisible(e.relates, submitData) && e.type !== 'schoolLogo');

    // AD-2691: the components shown in Preview Form and on the form being completed, in their rows.
    const inRows = (fields = [], nodes = []) => {
        const shown = fields.map((field, at) => ({field, node: nodes[at]})).filter(item => item.node);
        return groupIntoRows(shown.map(item => item.field)).map(row => (
            <FormRow key={`row-${row.key}`} widths={row.items.map(item => item.field.layout?.width ?? 100)}>
                {row.items.map(item => shown[item.index].node)}
            </FormRow>
        ));
    };

    return (
        submitMode
            ? <Box display="flex" flexDirection="column" sx={{
                mx: publicForm
                    ? {
                        xs: 2,
                        sm: 0
                    }
                    : 0
            }}>
                {inRows(submitFields, submitFields.map((f, index) => {

                        const hiddenTypes = ['intakeChoice', 'boarderStatusChoice'];

                        const validOptionCount = f.options?.filter(
                            o => o.value !== '' && o.value != null
                        ).length ?? 0;

                        const shouldHideVisually =
                            hiddenTypes.includes(f.type) && (validOptionCount === 0 || validOptionCount === 1);

                        if (f.type === 'schoolLogo') {
                            return null;
                        }

                        const isSeparator = f.type === 'separator';

                        const content = (
                            <ComponentBuilder
                                handleChangeSubmit={handleChangeSubmit}
                                handleDownloadAttachment={handleDownloadAttachment}
                                index={index}
                                readOnly={readOnly}
                                handleChangeEdit={handleChangeEdit}
                                previewMode={componentToEditIndex === index || previewMode}
                                formPreviewMode={previewMode}
                                field={f}
                                componentToEditIndex={componentToEditIndex}
                                data={data}
                                submitMode={submitMode}
                                submitData={submitData}
                                isFormArchived={isFormArchived}
                                accessLevel={accessLevel}
                                relatedComponent={relatedFormQuestionAssignData?.find(e => e.relatedComponentId === f.id)}
                                error={submitMode ? errors[f.id]?.error : schemaErrors[f.id]}
                                audience={audience}
                                recipientType={recipientType}
                                linkedToStudents={linkedToStudents}
                                linkedToEmployees={linkedToEmployees}
                                formRequestId={formRequestId}
                                formId={formId}
                                schema={schema}
                                onEmailCheck={onEmailCheck}
                                warnings={warnings}
                                setWarningFor={setWarningFor}
                                placeholderValues={editorPlaceholderValues}
                                getDraftDescriptionHtml={getDraftDescriptionHtml}
                                setDraftDescriptionHtml={setDraftDescriptionHtml}
                                commitDraftDescriptionToSchema={commitDraftDescriptionToSchema}
                            />
                        );

                        return isSeparator
                            ? (
                                <Box key={`formItem-${f.id}`} id={`form-field-${f.id}`} sx={{
                                    my: 6,
                                }}>
                                    {content}
                                </Box>
                            )
                            : (
                                <Box
                                    key={`formItem-${f.id}`}
                                    id={`form-field-${f.id}`}
                                    sx={{
                                        my: 1,
                                        px: 3,
                                        py: 6,
                                        border: '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        backgroundColor: '#ffffff',
                                        display: shouldHideVisually ? 'none' : 'block',
                                    }}
                                >
                                    {content}
                                </Box>
                            );
                    }))
                }
                <SubmitProblemDialog
                    open={!!submitProblems}
                    onClose={() => setSubmitProblems(null)}
                    missing={submitProblems?.missing}
                    format={submitProblems?.format}
                    payment={!!submitProblems?.payment}
                    medicine={submitProblems?.medicine}
                    sections={submitProblems?.sections}
                    childName={placeholderValues?.StudentForename || placeholderValues?.studentForename}
                    profileHref={profileStudentId ? `/profiles/students/${profileStudentId}` : undefined}
                    onGoTo={goToQuestion}
                />
                {
                    <Box>
                        {!readOnly && !previewMode && !publicForm && (
                            <Divider sx={{my: 1}}/>
                        )}
                        <Box display="flex" justifyContent="end">
                            {
                                readOnly || previewMode || isCommunication
                                    ? null
                                    : <Button
                                        sx={{
                                            mt: 1,
                                            mb: 2,
                                            backgroundColor: '#186BBE',
                                            boxShadow: 'none',
                                            width: {
                                                xs: '100%',
                                                sm: 'auto'
                                            },
                                            '&:hover': {
                                                backgroundColor: '#155FA7',
                                                boxShadow: 'none'
                                            }
                                        }}
                                        variant="contained"
                                        onClick={onSubmit}
                                    >
                                        Submit
                                    </Button>
                            }
                        </Box>
                    </Box>
                }
            </Box>
            : <Box onDoubleClick={() => submitMode
                ? () => {
                }
                : setComponentToEditIndex(undefined)}>
                {(title || !isPreviewControlled) && (
                <Box display={'flex'} justifyContent={title ? 'space-between' : 'end'} alignItems={'center'}
                     sx={title ? {px: 3, pt: 1} : undefined}>
                    {title && (
                        <Typography variant="body2" color="text.secondary" data-testid="form-builder-title">
                            {title}
                        </Typography>
                    )}
                    {!submitMode && !isFormArchived && !isPreviewControlled && (
                        <Tooltip
                            title={componentToEditIndex >= 0
                                ? 'Preview is disabled while editing components. Double-click outside the component area to exit edit mode and enable the preview.'
                                : null}
                        >
            <span>
                <Button
                    disabled={componentToEditIndex >= 0}
                    onClick={() =>
                        componentToEditIndex >= 0
                            ? setComponentToEditIndex(undefined)
                            : setPreviewMode(!previewMode)
                    }
                    sx={{width: '100px'}}
                >
                    {previewMode ? 'Edit' : 'Preview'}
                </Button>
            </span>
                        </Tooltip>
                    )}
                </Box>
                )}

                <DragDropContext onDragEnd={onDragEnd}>
                <Box
                    ref={paletteRowRef}
                    display={'flex'}
                    justifyContent={'center'}
                    alignItems={'flex-start'}
                    sx={{
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0
                    }}
                >
                    {showPalette && (
                        <Box
                            ref={paletteRef}
                            data-testid="builder-palette"
                            sx={{
                                width: 250,
                                flexShrink: 0,
                                position: 'sticky',
                                top: paletteTop,
                                // AD-2706: short enough to fit inside the white panel when the page is at the top, so a
                                // short form does not scroll; the list scrolls inside itself.
                                maxHeight: isWizardMode ? `calc(100vh - ${paletteTop} - 32px)` : 'calc(100vh - 310px)',
                                overflowY: 'auto',
                                alignSelf: 'flex-start',
                                boxSizing: 'border-box',
                                mt: 2,
                                ml: {xs: 1, sm: 2},
                                p: 1.5,
                                border: '1px solid #e3e8ef',
                                borderRadius: '8px',
                                backgroundColor: '#f9fafb',
                            }}
                        >
                            {Object.values(ComponentCategory).map((category) => {
                                const items = (formComponentsByCategory[category] || []).filter(isComponentOffered);

                                if (!items.length) return null;

                                const isStudentProfileAttributes = category === ComponentCategory.STUDENT_PROFILE_ATTRIBUTES;

                                return (
                                    <Box key={category} sx={{mb: 1.5}}>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5}}>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                {category}
                                            </Typography>
                                            {isStudentProfileAttributes && (
                                                <Tooltip
                                                    title="When these attributes are added to a form and the form is linked with students, the respective values will be automatically updated on the student profile once the form is submitted."
                                                    placement="right"
                                                    arrow
                                                >
                                                    <InfoOutlinedIcon
                                                        fontSize="small"
                                                        sx={{color: 'text.secondary', cursor: 'help'}}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Box>
                                        <Droppable
                                            droppableId={`${PALETTE_DROPPABLE_PREFIX}${category}`}
                                            isDropDisabled
                                        >
                                            {(provided) => (
                                                <Box ref={provided.innerRef} {...provided.droppableProps}>
                                                    {items.map(renderPaletteItem)}
                                                    {provided.placeholder}
                                                </Box>
                                            )}
                                        </Droppable>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                    <Box
                        sx={{
                            flex: 1,
                            width: '100%',
                            maxWidth: {
                                xs: '100%',
                                lg: '1200px'
                            },
                            minWidth: 0,
                            p: 2,
                            mx: {
                                xs: 0,
                                sm: 2,
                                md: showPalette ? 3 : '180px'
                            },
                            mb: 2
                        }}
                    >
                        {/* The school logo is shown in Preview and on the form people receive, not while the form is being built. */}
                        {schoolLogoComponent?.url && (previewMode || readOnly || isFormArchived) && (
                            <Box textAlign="center" sx={{
                                mx: {
                                    xs: 2,
                                    sm: 0
                                },
                                my: 2,
                                px: 3,
                                py: 6,
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                backgroundColor: '#ffffff',
                            }}>
                                <ImageWithConditionalRender
                                    imageUrl={schoolLogoComponent.url}
                                    alt="School logo"
                                />
                            </Box>
                        )}
                        {!previewMode
                            ? <Droppable droppableId="droppable" isCombineEnabled={!isFormArchived && !isCommunication}>
                                    {(provided) => (
                                        <Box
                                            data-testid="builder-canvas"
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                        >
                                            {builderRows.map((row, rowIndex) => {
                                                const rowFull = row.items.length >= MAX_PER_ROW;
                                                return (
                                                <Draggable
                                                    key={row.key}
                                                    draggableId={row.key}
                                                    index={rowIndex}
                                                    isDragDisabled={isFormArchived || isCommunication}
                                                >
                                                    {(provided, snapshot) => (
                                                        <Box
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            data-testid={toTestId('builder-row', rowIndex + 1)}
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: {xs: 'column', sm: 'row'},
                                                                alignItems: 'stretch',
                                                                position: 'relative',
                                                                borderRadius: '8px',
                                                                // AD-2691: dropping on a component puts the two side by side.
                                                                ...(snapshot.combineTargetFor
                                                                    ? {outline: `2px dashed ${rowFull ? '#d32f2f' : '#186BBE'}`, outlineOffset: '2px'}
                                                                    : {}),
                                                            }}
                                                        >
                                                            {snapshot.combineTargetFor && (
                                                                <Box sx={{position: 'absolute', zIndex: 2, top: -10, left: 16, px: 1, fontSize: 12, borderRadius: 1, color: '#fff', backgroundColor: rowFull ? '#d32f2f' : '#186BBE'}}>
                                                                    {rowFull ? 'A row can have up to three components' : 'Drop to place side by side'}
                                                                </Box>
                                                            )}
                                                            {row.items.map(({field: f, index}, position) => (
                                                                <React.Fragment key={f.id}>
                                                                    {position > 0 && (
                                                                        <Tooltip title="Drag to change the widths" followCursor>
                                                                            <Box
                                                                                component="button"
                                                                                type="button"
                                                                                aria-label="Drag to change the widths"
                                                                                data-testid={toTestId('builder-resize', index + 1)}
                                                                                disabled={isFormArchived || isCommunication}
                                                                                onMouseDown={(event) => startResize(event, row.items[position - 1].field, f)}
                                                                                sx={{
                                                                                    display: {xs: 'none', sm: 'block'},
                                                                                    flex: '0 0 16px',
                                                                                    my: 1,
                                                                                    p: 0,
                                                                                    border: 0,
                                                                                    background: 'transparent',
                                                                                    cursor: 'col-resize',
                                                                                    position: 'relative',
                                                                                    '&::after': {
                                                                                        content: '""',
                                                                                        position: 'absolute',
                                                                                        top: '20%',
                                                                                        bottom: '20%',
                                                                                        left: '6px',
                                                                                        width: '4px',
                                                                                        borderRadius: '2px',
                                                                                        backgroundColor: '#c5cdd8',
                                                                                    },
                                                                                    '&:hover::after': {backgroundColor: '#186BBE'},
                                                                                }}
                                                                            />
                                                                        </Tooltip>
                                                                    )}
                                                                    <Box sx={{
                                                                        flex: row.items.length > 1
                                                                            ? {xs: '1 1 auto', sm: `${widthOf(f)} 1 0`}
                                                                            : '1 1 auto',
                                                                        minWidth: 0,
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        '& > *': {flexGrow: 1},
                                                                    }}>
                                                        <Box
                                                            key={`formItem-${f.id}`}
                                                            data-testid={toTestId('builder-field', index + 1)}
                                                            data-testfield={f.type}
                                                            sx={{
                                                                mx: {
                                                                    xs: 2,
                                                                    sm: 0
                                                                },
                                                                // AD-2689 (Maria): the least padding possible inside every component.
                                                                my: 1,
                                                                px: 2,
                                                                pt: 0.5,
                                                                pb: 1,
                                                                '& > :nth-of-type(n+2), & > :nth-of-type(n+2) > :first-of-type': {mt: 0},
                                                                // The boxes inside a component are compact too (the small size of a box).
                                                                '& .MuiOutlinedInput-input:not(.MuiInputBase-inputMultiline), & .MuiSelect-select': {
                                                                    paddingTop: '8.5px',
                                                                    paddingBottom: '8.5px',
                                                                },
                                                                '& .MuiInputBase-multiline': {paddingTop: '8.5px', paddingBottom: '8.5px'},
                                                                '& .MuiAutocomplete-root .MuiOutlinedInput-root': {paddingTop: 0, paddingBottom: 0},
                                                                '& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
                                                                    transform: 'translate(14px, 9px) scale(1)',
                                                                },
                                                                '& .MuiTableCell-root': {paddingTop: '4px', paddingBottom: '4px'},
                                                                // Small radio buttons and tick boxes (Maria: they took up a lot of space).
                                                                '& .MuiRadio-root, & .MuiCheckbox-root': {padding: '4px'},
                                                                '& .MuiRadio-root .MuiSvgIcon-root, & .MuiCheckbox-root .MuiSvgIcon-root': {fontSize: '1.125rem'},
                                                                '& .MuiFormControlLabel-root': {marginTop: 0, marginBottom: 0},
                                                                // Rows of boxes (the address) sit 8px apart, not 16px with 16px gaps above.
                                                                '& .MuiGrid-container.MuiGrid-spacing-xs-2': {
                                                                    marginTop: '-8px',
                                                                    marginLeft: '-8px',
                                                                    width: 'calc(100% + 8px)',
                                                                },
                                                                '& .MuiGrid-container.MuiGrid-spacing-xs-2 + .MuiGrid-container.MuiGrid-spacing-xs-2': {marginTop: 0},
                                                                '& .MuiGrid-container.MuiGrid-spacing-xs-2 > .MuiGrid-item': {paddingTop: '8px', paddingLeft: '8px'},
                                                                border: '1px solid #d1d5db',
                                                                borderRadius: '8px',
                                                                backgroundColor: '#ffffff',
                                                                borderColor: schemaErrors[f.id] ? 'error.main' : '',
                                                                // AD-2689 (Maria): a blue frame round the component being edited.
                                                                ...(componentToEditIndex === index
                                                                    ? {
                                                                    borderColor: schemaErrors[f.id] ? 'error.main' : '#186BBE',
                                                                    boxShadow: '0 0 0 2px #186BBE',
                                                                    backgroundColor: '#f5f9fd',
                                                                    }
                                                                    : {}),
                                                            }}
                                                        >
                                                            {buildEditRows(f, index)}
                                                            {/* AD-2689 (Maria): before a component is opened, an empty question box shows its grey text. */}
                                                            {componentToEditIndex !== index && !isFormArchived &&
                                                                !stripHtmlTags(f.description || '').trim() &&
                                                                !NO_QUESTION_BOX_TYPES.has(f.type) && (
                                                                <Box sx={{border: '1px solid #e3e8ef', borderRadius: 1, p: 1, mb: 1, color: 'text.disabled'}}>
                                                                    {QUESTION_BOX_TEXT[f.type] || DEFAULT_QUESTION_BOX_TEXT}
                                                                </Box>
                                                            )}
                                                            {/* AD-2689 (Maria): hover text says where the question goes, instead of text inside the component. */}
                                                            <Tooltip
                                                                followCursor
                                                                title={componentToEditIndex !== index && !isFormArchived
                                                                    ? (stripHtmlTags(f.description || '').trim()
                                                                        ? 'Click on the pencil icon to edit this component.'
                                                                        : 'Click on the pencil icon to add the question.')
                                                                    : ''}
                                                            >
                                                            <Box>
                                                            <ComponentBuilder
                                                                handleChangeSubmit={handleChangeSubmit}
                                                                index={index}
                                                                readOnly={readOnly}
                                                                handleChangeEdit={handleChangeEdit}
                                                                previewMode={componentToEditIndex !== index || previewMode}
                                                                formPreviewMode={previewMode}
                                                                field={f}
                                                                componentToEditIndex={componentToEditIndex}
                                                                data={data}
                                                                submitMode={submitMode}
                                                                submitData={submitData}
                                                                isFormArchived={isFormArchived}
                                                                accessLevel={accessLevel}
                                                                relatedComponent={relatedFormQuestionAssignData
                                                                    ?.find(e => e.relatedComponentId === f.id)}
                                                                error={submitMode ? errors[f.id]?.error : schemaErrors[f.id]}
                                                                audience={audience}
                                                                parentAudienceType={parentAudienceType}
                                                                recipientType={recipientType}
                                                                linkedToStudents={linkedToStudents}
                                                                linkedToEmployees={linkedToEmployees}
                                                                formId={formId}
                                                                schema={schema}
                                                                placeholderValues={editorPlaceholderValues}
                                                                getDraftDescriptionHtml={getDraftDescriptionHtml}
                                                                setDraftDescriptionHtml={setDraftDescriptionHtml}
                                                                commitDraftDescriptionToSchema={commitDraftDescriptionToSchema}
                                                            />
                                                            </Box>
                                                            </Tooltip>
                                                        </Box>
                                                                    </Box>
                                                                </React.Fragment>
                                                            ))}
                                                        </Box>
                                                    )}
                                                </Draggable>
                                                );
                                            })}
                                            {provided.placeholder}
                                        </Box>)}
                                </Droppable>
                            : (
                                <Box>
                                    {inRows(previewFields, previewFields.map((f, index) => {
                                            const hiddenTypes = ['intakeChoice', 'boarderStatusChoice'];

                                            const validOptionCount = f.options?.filter(
                                                o => o.value !== '' && o.value != null
                                            ).length ?? 0;

                                            const shouldHideVisually =
                                                hiddenTypes.includes(f.type) &&
                                                (validOptionCount === 0 || validOptionCount === 1);

                                            if (shouldHideVisually) {
                                                return null;
                                            }

                                            const isSeparator = f.type === 'separator';

                                            const content = (
                                                <ComponentBuilder
                                                    handleChangeSubmit={handleChangeSubmit}
                                                    handleDownloadAttachment={handleDownloadAttachment}
                                                    index={index}
                                                    readOnly={readOnly}
                                                    handleChangeEdit={handleChangeEdit}
                                                    previewMode={componentToEditIndex === index || previewMode}
                                                    formPreviewMode={previewMode}
                                                    field={f}
                                                    componentToEditIndex={componentToEditIndex}
                                                    data={data}
                                                    submitMode={submitMode}
                                                    submitData={submitData}
                                                    isFormArchived={isFormArchived}
                                                    accessLevel={accessLevel}
                                                    placeholderValues={editorPlaceholderValues}
                                                    error={submitMode ? errors[f.id]?.error : schemaErrors[f.id]}
                                                    audience={audience}
                                                    parentAudienceType={parentAudienceType}
                                                    recipientType={recipientType}
                                                    linkedToStudents={linkedToStudents}
                                                    linkedToEmployees={linkedToEmployees}
                                                    formId={formId}
                                                    schema={schema}
                                                    getDraftDescriptionHtml={getDraftDescriptionHtml}
                                                    setDraftDescriptionHtml={setDraftDescriptionHtml}
                                                    commitDraftDescriptionToSchema={commitDraftDescriptionToSchema}
                                                />
                                            );

                                            return isSeparator
                                                ? (
                                                    <Box key={`formItem-${f.id}`}
                                                         sx={{
                                                             my: 7,
                                                             borderRadius: '8px',
                                                         }}>
                                                        {content}
                                                    </Box>
                                                )
                                                : (
                                                    <Box
                                                        key={`formItem-${f.id}`}
                                                        sx={{
                                                            my: 2,
                                                            px: 3,
                                                            py: 6,
                                                            // AD-2689 (Maria): small radio buttons and tick boxes in Preview Form too.
                                                            '& .MuiRadio-root, & .MuiCheckbox-root': {padding: '4px'},
                                                            '& .MuiRadio-root .MuiSvgIcon-root, & .MuiCheckbox-root .MuiSvgIcon-root': {fontSize: '1.125rem'},
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '8px',
                                                            backgroundColor: '#ffffff',
                                                            display: shouldHideVisually ? 'none' : 'block',
                                                        }}
                                                    >
                                                        {content}
                                                    </Box>
                                                );
                                        }))}
                                </Box>
                            )
                        }
                    </Box>
                    <AdmicityDialog
                        handleClose={handleDialogClose}
                        title={'Show Field When'}
                        actions={[]}
                        open={Boolean(currentComponent)}
                        maxWidth={'md'}
                    >
                        <ShowFieldWhen component={currentComponent} handleShowFieldWhen={handleShowFieldWhen}
                                       availableComponents={relatedComponents.filter(c => c.id !== currentComponent?.id)}/>
                    </AdmicityDialog>
                    <ConfirmRemoveDialog
                        open={Boolean(componentToConfirmRemove)}
                        handleClose={() => setComponentToConfirmRemove(undefined)}
                        handleConfirm={componentToConfirmRemove?.handleRemove}
                        relatedComponents={componentToConfirmRemove?.components}/>
                </Box>
                </DragDropContext>
            </Box>);
};

FormBuilder.propTypes = {
    title: PropTypes.string,
    templateMode: PropTypes.bool,
    isWizardMode: PropTypes.bool,
    schema: PropTypes.array,
    schemaId: PropTypes.number,
    onSchemaChange: PropTypes.func,
    onSchemaValidationChange: PropTypes.func,
    submitMode: PropTypes.bool,
    handleSubmit: PropTypes.func,
    handleDownloadAttachment: PropTypes.func,
    data: PropTypes.object,
    readOnly: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    accessLevel: PropTypes.number,
    placeholderValues: PropTypes.object,
    formId: PropTypes.number,
    componentToEditIndex: PropTypes.number,
    setComponentToEditIndex: PropTypes.func,
    publicForm: PropTypes.bool,
    isCommunication: PropTypes.bool,
    audience: PropTypes.number,
    parentAudienceType: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
    triggerSourceAudience: PropTypes.number,
    formRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onEmailCheck: PropTypes.func,
    formAccessLevel: PropTypes.number,
    getDraftDescriptionHtml: PropTypes.func,
    setDraftDescriptionHtml: PropTypes.func,
    commitDraftDescriptionToSchema: PropTypes.func,
    blockedForViewer: PropTypes.bool,
    needsToConfirmSections: PropTypes.string,
    profileStudentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    previewMode: PropTypes.bool,
    onPreviewModeChange: PropTypes.func,
    formTemplateType: PropTypes.string,
};

export default FormBuilder;
